import { useEffect, useMemo } from 'react';
import { useCallsSeries } from '@/hooks/useCallsSeries';
import { useMeaningfulCallsSeries } from '@/hooks/useMeaningfulCallsSeries';
import { useEmailsPersonalSeries } from '@/hooks/useEmailsPersonalSeries';
import { useEmailsCampaignSeries } from '@/hooks/useEmailsCampaignSeries';
import { useContactsSeries } from '@/hooks/useContactsSeries';
import { useConnectionsSeries } from '@/hooks/useConnectionsSeries';
import type { TimeSeriesResult, TimeSeriesBucket } from '@/hooks/useTimeseriesQuery';
import type { Granularity } from '@/lib/analytics/metrics';
import type { EmailSubtype } from '@/lib/analytics/seriesConfig';

// Each series-config kind needs its own hook — calling distinct hooks
// conditionally would violate rules-of-hooks, so we render one wrapper
// component per kind. Each wrapper calls exactly one hook and reports its
// result up via onData. Returns null; this component is purely a fetch +
// report side effect.
//
// useTimeseriesQuery rebuilds its wrapper (incl. the buckets array) every
// render, so depending on `result.buckets` reference would fire onData
// every render and loop with parent state. Deps below use only the scalar
// invariants (isLoading, error, total_current) — total_current is a sum
// over buckets and changes whenever the data does.

export interface SharedSeriesArgs {
  workspaceId: string;
  from: Date;
  to: Date;
  compareFrom: Date | null;
  compareTo: Date | null;
  manualGranularity: Granularity | 'auto';
  userFilter: string | null;
  tz: string;
}

interface FetcherProps {
  id: string;
  shared: SharedSeriesArgs;
  onData: (id: string, result: TimeSeriesResult) => void;
}

interface CallsFetcherProps extends FetcherProps {
  outcomes?: string[];
}

export function CallsFetcher({ id, shared, outcomes, onData }: CallsFetcherProps) {
  const result = useCallsSeries({ ...shared, outcomeFilter: outcomes });
  useEffect(() => {
    onData(id, result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, result.isLoading, result.error, result.total_current]);
  return null;
}

export function MeaningfulCallsFetcher({ id, shared, onData }: FetcherProps) {
  const result = useMeaningfulCallsSeries(shared);
  useEffect(() => {
    onData(id, result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, result.isLoading, result.error, result.total_current]);
  return null;
}

export function PersonalEmailsFetcher({ id, shared, onData }: FetcherProps) {
  const result = useEmailsPersonalSeries(shared);
  useEffect(() => {
    onData(id, result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, result.isLoading, result.error, result.total_current]);
  return null;
}

export function CampaignEmailsFetcher({ id, shared, onData }: FetcherProps) {
  const result = useEmailsCampaignSeries(shared);
  useEffect(() => {
    onData(id, result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, result.isLoading, result.error, result.total_current]);
  return null;
}

export function NewContactsFetcher({ id, shared, onData }: FetcherProps) {
  const result = useContactsSeries(shared);
  useEffect(() => {
    onData(id, result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, result.isLoading, result.error, result.total_current]);
  return null;
}

interface NewConnectionsFetcherProps extends FetcherProps {
  relationships?: string[];
}

export function NewConnectionsFetcher({
  id,
  shared,
  relationships,
  onData,
}: NewConnectionsFetcherProps) {
  const result = useConnectionsSeries({ ...shared, relationshipFilter: relationships });
  useEffect(() => {
    onData(id, result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, result.isLoading, result.error, result.total_current]);
  return null;
}

interface EmailsFetcherProps extends FetcherProps {
  subtypes?: EmailSubtype[];
}

// Email is the unified Activities → Email leaf. Default subtype filter is
// "both"; selecting one drops the other from the merged result. Both hooks
// always run to satisfy rules-of-hooks; the inactive one's contribution is
// simply not summed.
export function EmailsFetcher({ id, shared, subtypes, onData }: EmailsFetcherProps) {
  const personal = useEmailsPersonalSeries(shared);
  const campaign = useEmailsCampaignSeries(shared);

  const includePersonal = !subtypes || subtypes.length === 0 || subtypes.includes('personal');
  const includeCampaign = !subtypes || subtypes.length === 0 || subtypes.includes('campaign');

  const merged: TimeSeriesResult = useMemo(() => {
    const sources: TimeSeriesResult[] = [];
    if (includePersonal) sources.push(personal);
    if (includeCampaign) sources.push(campaign);
    return mergeTimeSeriesResults(sources, personal.granularity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    includePersonal,
    includeCampaign,
    personal.isLoading,
    personal.error,
    personal.total_current,
    personal.granularity,
    campaign.isLoading,
    campaign.error,
    campaign.total_current,
  ]);

  useEffect(() => {
    onData(id, merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, merged.isLoading, merged.error, merged.total_current]);
  return null;
}

function mergeTimeSeriesResults(
  sources: TimeSeriesResult[],
  granularity: Granularity,
): TimeSeriesResult {
  const isLoading = sources.some((s) => s.isLoading);
  const error = sources.find((s) => s.error)?.error ?? null;

  const byDate = new Map<string, { current: number; previous: number | null }>();
  for (const s of sources) {
    for (const b of s.buckets) {
      const acc = byDate.get(b.bucket_date) ?? { current: 0, previous: null };
      acc.current += b.current;
      if (b.previous !== null) acc.previous = (acc.previous ?? 0) + b.previous;
      byDate.set(b.bucket_date, acc);
    }
  }

  const buckets: TimeSeriesBucket[] = Array.from(byDate.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([bucket_date, v]) => ({ bucket_date, current: v.current, previous: v.previous }));

  const total_current = sources.reduce((sum, s) => sum + s.total_current, 0);
  const previous_vals = sources.map((s) => s.total_previous).filter((x): x is number => x !== null);
  const total_previous = previous_vals.length > 0 ? previous_vals.reduce((a, b) => a + b, 0) : null;
  const delta_pct =
    total_previous === null ? null
      : total_previous === 0 && total_current === 0 ? 0
      : total_previous === 0 ? null
      : Math.round(((total_current - total_previous) / total_previous) * 1000) / 10;

  return {
    kind: 'time-series',
    buckets,
    total_current,
    total_previous,
    delta_pct,
    granularity,
    isLoading,
    error,
  };
}
